'use client';

import { FC, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import type { RecipeDto } from '@/api/ApiClient';
import Recipe from '@/components/recipe/Recipe';
import { mapRecipeStateToDto } from '@/lib/types/recipeMapper';
import { useRecipeEditor } from '@/components/recipe/hooks/useRecipeEditor';

/**
 * Configuration for RecipeEditorDialog
 */
export interface RecipeEditorDialogConfig {
  /** Dialog title */
  title?: string;
  /** Activity type (e.g., 'preparation', 'synthesis', 'compounding') */
  activityType?: string;
  /** Technique ID */
  technique?: string;
  /** Instrument ID */
  instrument?: string;
  /** Initial recipe data */
  recipe?: RecipeDto;
  /** Recipe template name */
  recipeTemplate?: string;
  /** Whether the editor is readonly */
  isReadOnly?: boolean;
  /** Optional info line to display above the recipe */
  infoLine?: string;
  /** Callback when recipe is saved */
  onSave?: (recipe: RecipeDto) => Promise<void>;
  /** Callback when dialog is closed */
  onClose?: () => void;
}

/**
 * General Recipe Editor Dialog
 *
 * A reusable dialog component for editing recipes. Can be used for:
 * - Bulk sample recipes
 * - Recipe favorites
 * - Any other recipe editing scenario
 */
export const RecipeEditorDialog: FC<{
  open: boolean;
  config: RecipeEditorDialogConfig;
}> = ({ open, config }) => {
  const { showSnackbar } = useSnackbar();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use shared recipe editor hook
  const recipeEditor = useRecipeEditor({
    activity:
      config.recipe || config.recipeTemplate
        ? {
            type: config.activityType,
            technique: config.technique,
            instrument: config.instrument,
            recipe: config.recipe,
            recipeTemplate: config.recipeTemplate,
          }
        : undefined,
    autoLoadTemplates: !!(config.recipe || config.recipeTemplate),
    isReadOnly: config.isReadOnly,
  });

  // Handle save
  const handleSave = useCallback(async () => {
    if (!recipeEditor.recipeStore || recipeEditor.isReadOnly || !config.onSave) return;

    setSaving(true);
    setError(null);
    try {
      const recipeDto = mapRecipeStateToDto(recipeEditor.recipeStore.toState());
      await config.onSave(recipeDto);
      showSnackbar('Recipe saved successfully', 'success');
      // Clean up state after successful save
      setError(null);
      recipeEditor.setRecipeData(undefined);
      recipeEditor.setFullRecipeTemplate(null);
      config.onClose?.();
    } catch (err: any) {
      log.error('[RecipeEditorDialog] Error saving recipe:', err);
      setError(getProblemDetail(err) ?? 'Failed to save recipe');
      showSnackbar(getProblemDetail(err) ?? 'Failed to save recipe', 'error');
      setSaving(false);
      // Don't close dialog on error - let user retry or cancel
    }
  }, [recipeEditor, config, showSnackbar]);

  // Handle close
  const handleClose = useCallback(() => {
    // Clean up all state when closing
    setError(null);
    recipeEditor.setRecipeData(undefined);
    recipeEditor.setFullRecipeTemplate(null);
    config.onClose?.();
  }, [config, recipeEditor]);

  const dialogTitle = config.title || 'Edit Recipe';

  if (!open) return null;

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      onSubmit={handleSave}
      submitText="Save"
      submitDisabled={recipeEditor.isReadOnly || !recipeEditor.recipeStore || saving || !config.onSave}
      isLoading={saving}
      maxWidth="lg"
      showDefaultActions={true}
    >
      {(recipeEditor.isInitializingRecipeStore || recipeEditor.loadingTemplates) && !error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box sx={{ mt: 2 }}>
          {/* Optional info line */}
          {config.infoLine ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {config.infoLine}
            </Typography>
          ) : null}

          {/* Recipe editor */}
          {recipeEditor.recipeTemplateDTO && recipeEditor.recipeData && recipeEditor.recipeStore ? (
            <Box sx={{ mt: 2 }}>
              <Recipe store={recipeEditor.recipeStore} readonly={recipeEditor.isReadOnly} />
            </Box>
          ) : !recipeEditor.recipeTemplateDTO ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              {recipeEditor.loadingTemplates ? (
                <>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading recipe templates...
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Select recipe template first
                </Typography>
              )}
            </Box>
          ) : (
            // Loading recipe editor (template loaded but recipe store not yet initialized)
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      )}
    </StandardDialog>
  );
};
